<script>
import axios from "axios";
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import {computed, onMounted, ref} from "vue";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import {usePaySectionStore} from "@/stores/PaySection";
import {useImportModelStore} from "@/stores/ImportModel";
import useXLSXHeaderReference from "@/views/ImportModel/use/XLSXHeaderReference";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const {$route, $refs} = useTools();
        const {dialog, loading, overlay} = useSharedData();
        const {getBack} = useSharedMethod();
        
        const readonly = computed(() => props.deleteAction || props.viewAction);
        
        const paySectionStore = usePaySectionStore();
        const importModelStore = useImportModelStore();
        
        const {headers, headerExample, address} = useXLSXHeaderReference();
        
        const importModel = ref({
            startReadingFromLine: 2,
            settings: [],
        });
        
        function setStartReadingLine(line) {
            if (!readonly.value)
                importModel.value.startReadingFromLine = line;
        }
        
        const attachFileExampleDialog = ref(false);
        const attachFileExampleLoading = ref(false);
        const titleLigne = ref(1);
        const file = ref(null);
        
        function attach() {
            if ($refs.form3.validate()) {
                attachFileExampleLoading.value = true;
                const formData = new FormData();
                formData.append("file", file.value);
                axios.post("/importModels/mapXLSXHeaderToList", formData, {
                    params: {
                        titleLigne: titleLigne.value,
                    },
                }).then((response) => {
                    headerExample.value = response.data;
                    attachFileExampleDialog.value = false;
                }).finally(() => {
                    attachFileExampleLoading.value = false;
                });
            }
        }
        
        const headerDialog = ref(false);
        const selectedHeader = ref(null);
        const option = ref(null);
        const code = ref(null);
        
        function openHeader(h) {
            if (!readonly.value) {
                selectedHeader.value = h;
                headerDialog.value = true;
            }
        }
        
        function setOption() {
            if ($refs.form2.validate()) {
                switch (option.value) {
                    case "NONE":
                        address.value[`${selectedHeader.value}`] = null;
                        break;
                    case "CODE":
                        address.value[`${selectedHeader.value}`] = "CODE";
                        break;
                    case "NOM_PRENOM":
                        address.value[`${selectedHeader.value}`] = "NOM_PRENOM";
                        break;
                    case "FONCTION":
                        address.value[`${selectedHeader.value}`] = "FONCTION";
                        break;
                    case "ORGANIZAGRAMME":
                        address.value[`${selectedHeader.value}`] = "ORGANIZAGRAMME";
                        break;
                    case "RUBRIQUE":
                        address.value[`${selectedHeader.value}`] = code.value;
                        break;
                }
                headerDialog.value = false;
                option.value = null;
                code.value = null;
            }
        }
        
        function translateCode(code) {
            const matchingPaySection = paySectionStore.paySections.find(element => element.code === code);
            return matchingPaySection ? matchingPaySection.codeLabel : code;
        }
        
        function findById(id) {
            loading.value = true;
            axios.get(`/importModels/${id}`).then((response) => {
                importModel.value = response.data;
                
                if (importModel.value.codeAddress) {
                    address.value[`${importModel.value.codeAddress}`] = "CODE";
                    headerExample.value[`${importModel.value.codeAddress}`] = importModel.value.realCodeLabel;
                }
                
                if (importModel.value.fullNameAddress) {
                    address.value[`${importModel.value.fullNameAddress}`] = "NOM_PRENOM";
                    headerExample.value[`${importModel.value.fullNameAddress}`] = importModel.realFullNameLabel;
                }
                
                if (importModel.value.occupationAddress) {
                    address.value[`${importModel.value.occupationAddress}`] = "FONCTION";
                    headerExample.value[`${importModel.value.occupationAddress}`] = importModel.realOccupationLabel;
                }
                
                if (importModel.value.organizationalChartAddress) {
                    address.value[`${importModel.value.organizationalChartAddress}`] = "ORGANIZAGRAMME";
                    headerExample.value[`${importModel.value.organizationalChartAddress}`] = importModel.realOrganizationalChartLabel;
                }
                
                importModel.value.settings.forEach((setting) => {
                    address.value[`${setting.columnAddress}`] = setting.paySectionCode;
                    headerExample.value[`${setting.columnAddress}`] = setting.realHeader;
                });
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function buildInputObject() {
            importModel.value.settings = [];
            for (const key in address.value) {
                if (address.value[key]) {
                    switch (address.value[key]) {
                        case "CODE":
                            importModel.value.codeAddress = key;
                            importModel.value.realCodeLabel = headerExample.value[key];
                            break;
                        case "NOM_PRENOM":
                            importModel.value.fullNameAddress = key;
                            importModel.value.realFullNameLabel = headerExample.value[key];
                            break;
                        case "FONCTION":
                            importModel.value.occupationAddress = key;
                            importModel.value.realOccupationLabel = headerExample.value[key];
                            break;
                        case "ORGANIZAGRAMME":
                            importModel.value.organizationalChartAddress = key;
                            importModel.value.realOrganizationalChartLabel = headerExample.value[key];
                            break;
                        default:
                            importModel.value.settings.push({
                                columnAddress: key,
                                paySectionCode: address.value[key],
                                realHeader: headerExample.value[key],
                            });
                            break;
                    }
                }
            }
        }
        
        function save() {
            if ($refs.form1.validate()) {
                loading.value = true;
                buildInputObject();
                axios.post('/importModels', importModel.value).then((response) => {
                    importModelStore.addImportModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form1.validate()) {
                loading.value = true;
                buildInputObject();
                axios.put('/importModels', importModel.value).then((response) => {
                    importModelStore.updateImportModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/importModels/${importModel.value.id}`).then(() => {
                importModelStore.deleteImportModel(importModel.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });
        
        return {
            mxRules,
            dialog,
            loading,
            overlay,
            readonly,
            headerDialog,
            attachFileExampleDialog,
            attachFileExampleLoading,
            titleLigne,
            file,
            importModel,
            selectedHeader,
            option,
            code,
            headers,
            headerExample,
            address,
            setStartReadingLine,
            attach,
            translateCode,
            openHeader,
            setOption,
            save,
            update,
            remove,
            getBack
        }
    }
}
;
</script>

<template>
    <div>
        <v-dialog
            v-model="attachFileExampleDialog"
            persistent
            max-width="500"
        >
            <v-card flat>
                <w-toolbar color="blue" :title="$t('common.file')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form3" lazy-validation>
                            <v-row justify="center" align="center">
                                <v-col cols="12">
                                    <w-label :label="$t('common.file')" required/>
                                    <w-file-input
                                        v-model="file"
                                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('pay_slip.modal_import_header_line')" required/>
                                    <w-text-field
                                        v-model="titleLigne"
                                        type="number"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="attachFileExampleDialog = false"/>
                    <w-btn
                        :loading="attachFileExampleLoading"
                        color="blue"
                        icon="mdi-attachment"
                        :text="$t('common.attach')"
                        @press="attach"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="headerDialog" persistent max-width="400">
            <v-card flat>
                <w-toolbar color="blue" :title="$t('common.select')"/>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form2" lazy-validation>
                            <v-row dense justify="center" align="center">
                                <v-col cols="12">
                                    <v-radio-group
                                        v-model="option"
                                        column
                                        :rules="[mxRules.required]"
                                    >
                                        <v-radio
                                            :label="$t('worker.code')"
                                            value="CODE"
                                        />
                                        <v-radio
                                            :label="`${$t('common.last_name')} && ${$t('common.first_name')}`"
                                            value="NOM_PRENOM"
                                        />
                                        <v-radio
                                            :label="$t('module.occupation')"
                                            value="FONCTION"
                                        />
                                        <v-radio
                                            :label="$t('module.organizational_chart')"
                                            value="ORGANIZAGRAMME"
                                        />
                                        <v-radio
                                            :label="$t('pay_slip.pay_section')"
                                            color="teal"
                                            value="RUBRIQUE"
                                        />
                                        <v-radio
                                            :label="$t('common.delete')"
                                            color="red"
                                            value="NONE"
                                        />
                                    </v-radio-group>
                                </v-col>
                                <v-col cols="12" v-if="option === 'RUBRIQUE'">
                                    <w-label :label="$t('pay_slip.pay_section')" required/>
                                    <text-field-pay-section
                                        v-model="code"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="headerDialog = false"/>
                    <w-btn
                        color="blue"
                        icon="mdi-hand-okay"
                        text="OK"
                        @press="setOption"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="1500"
            scrollable
        >
            <v-card flat>
                <w-dialog-toolbar
                    :add-action="addAction"
                    :update-action="updateAction"
                    :delete-action="deleteAction"
                    :view-action="viewAction"
                ></w-dialog-toolbar>
                <w-overlay
                    :loading="loading"
                    :overlay="overlay"
                    :delete-action="deleteAction"
                    @cancel="overlay = false"
                    @remove="remove"
                ></w-overlay>
                <v-card-text>
                    <v-container fluid>
                        <v-form ref="form1" lazy-validation>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('common.designation')" required/>
                                    <w-text-field
                                        v-model="importModel.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.description')"/>
                                    <w-text-field
                                        v-model="importModel.description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                        <br/>
                        <v-row dense>
                            <v-col cols="12">
                                <v-simple-table dense fixed-header height="350">
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th></th>
                                            <th class="text-center" v-for="h in headers" :key="h">
                                                <strong>
                                                    {{ h }}
                                                </strong>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td></td>
                                            <td
                                                class="text-center select"
                                                v-for="h in headers"
                                                :key="h"
                                                @click="openHeader(h)"
                                            >
                                                <strong>
                                                    {{ translateCode(address[`${h}`]) }}
                                                </strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <v-btn
                                                    v-if="!readonly"
                                                    icon
                                                    color="blue"
                                                    @click="attachFileExampleDialog = true"
                                                >
                                                    <v-icon>mdi-paperclip</v-icon>
                                                </v-btn>
                                            </td>
                                            <td
                                                class="text-center"
                                                style="background-color: #c5e1a5"
                                                v-for="h in headers"
                                                :key="h"
                                            >
                                                <strong>
                                                    {{ translateCode(headerExample[`${h}`]) }}
                                                </strong>
                                            </td>
                                        </tr>
                                        
                                        <tr v-for="n in 20" :key="n">
                                            <td>
                                                <strong>
                                                    {{ n }}
                                                </strong>
                                            </td>
                                            <td
                                                :style="n === importModel.startReadingFromLine ? 'background-color: #B2EBF2' : ''"
                                                class="text-left"
                                                :colspan="headers.length"
                                                @click="setStartReadingLine(n)"
                                            >
                                                <strong v-if="importModel.startReadingFromLine === n">
                                                    {{ `${$t('pay_slip.modal_import_start_read_at')} ${n}` }}
                                                </strong>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        :loading="loading"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<style scoped>
table tbody tr {
    height: 10px;
}

td,
th {
    white-space: nowrap;
    overflow: hidden;
    border: 1px solid #ddd;
    width: 250px;
}

.select {
    background-color: #fff3e0;
}

.select:hover {
    background-color: #80cbc4;
    cursor: pointer;
}
</style>